import React, { FC } from 'react';
import { View, Text, StyleProp, ViewStyle } from 'react-native';
import i18n from 'i18n-js';

import { isPhone } from '@helpers';
import { scale, notchTop, width } from '@components/frame';
import { font } from '@styles/fonts';
import ColorWrapper from '@styles/colors';
import { SETTINGS_HEADER_MARGIN_BOTTOM } from '@v2/pages/common/constants/base.constants';
import HeaderBackButton from './components/HeaderBackButton';
import HeaderMoreButton from './components/HeaderMoreButton';
import HeaderActionButton from './components/HeaderActionButton';
import HeaderEditButton from './components/HeaderEditButton';
import { ColorFunction } from '@styles/colors/types';
import { SettingsRouteNames } from '@components/settings/types';
import { ActionConfig } from './types';

export interface SettingsHeaderProps {
  C: ColorFunction;
  name: SettingsRouteNames;
  action?: () => void;
  header?: string;
  subHeader?: {
    color: string;
    text: string;
  };
  actionLoading?: boolean;
  actionActive?: boolean;
  actionConfig?: Record<string, unknown>;
  backBtnText?: string;
  style?: StyleProp<ViewStyle>;
  backButtonAction?: () => void;
}

const Header: FC<SettingsHeaderProps> = ({
  C,
  name,
  action,
  header,
  subHeader,
  actionLoading,
  actionActive,
  actionConfig = {},
  backBtnText,
  style,
  backButtonAction,
}) => {
  const backButtonProps = { backBtnText, backButtonAction };
  const moreButtonProps = { actionActive, action };
  const actionButtonProps = { C, actionLoading, action, actionConfig, name };
  const editButtonProps = { C, action, actionConfig, };

  const leftAction: ActionConfig = {
    Account: null,
    Users: null,
    Devices: null,
    Machines: null,
    Company: null,
    AccountDetails: () => <HeaderBackButton {...backButtonProps} />,
    EmailReports: () => <HeaderBackButton {...backButtonProps} />,
    UserDetails: () => <HeaderBackButton {...backButtonProps} />,
    DeviceDetails: () => <HeaderBackButton {...backButtonProps} />,
    MachineDetails: () => <HeaderBackButton {...backButtonProps} />,
    FileConnector: () => <HeaderBackButton {...backButtonProps} />,
    FileConnectorDetails: () => <HeaderBackButton {...backButtonProps} />,
    MachineConnection: () => <HeaderBackButton {...backButtonProps} />,
    MachinesGroupDetails: () => <HeaderBackButton {...backButtonProps} />,
    MachinesImnooDetails: () => <HeaderBackButton {...backButtonProps} />,
    MachinesOperationDetails: () => <HeaderBackButton {...backButtonProps} />,
    MachineAssignedOperations: () => <HeaderBackButton {...backButtonProps} />,
  };

  const rightAction: ActionConfig = {
    Account: null,
    AccountDetails: () => <HeaderActionButton {...actionButtonProps} />,
    EmailReports: null,
    UserDetails: null,
    DeviceDetails: null,
    MachineDetails: () => <HeaderEditButton {...editButtonProps} />,
    FileConnector: null,
    FileConnectorDetails: null,
    Company: null,
    Users: () => <HeaderActionButton {...actionButtonProps} />,
    Devices: () => <HeaderActionButton {...actionButtonProps} />,
    Machines: () => <HeaderActionButton {...actionButtonProps} />,
    MachineConnection: () => <HeaderActionButton {...actionButtonProps} />,
    MachinesGroupDetails: () => <HeaderMoreButton {...moreButtonProps} />,
    MachinesImnooDetails: null,
  };

  const headerName: Partial<Record<SettingsRouteNames, string>> = {
    Account: 'Account',
    AccountDetails: 'Account',
    EmailReports: 'Email Reports',
    Users: 'User',
    UserDetails: 'User Details',
    DeviceDetails: 'Device Details',
    Devices: 'Devices',
    Machines: 'Machines',
    MachineAssignedOperations: 'Machine Operations',
    MachineDetails: 'Machine Details',
    FileConnector: 'File Connector',
    FileConnectorDetails: 'File Connector Details',
    Company: 'Company',
    MachineConnection: 'Connection',
  };

  const truncateHeaderName: Partial<Record<SettingsRouteNames, boolean>> = {
    Account: false,
    AccountDetails: false,
    EmailReports: false,
    UserDetails: false,
    DeviceDetails: false,
    MachineDetails: false,
    FileConnector: false,
    FileConnectorDetails: false,
    Company: false,
    Users: false,
    Devices: false,
    Machines: false,
    MachineConnection: false,
    MachinesGroupDetails: false,
    MachinesImnooDetails: true,
  };

  return (
    <View style={{ width: '100%', paddingTop: notchTop }}>
      <View
        style={[
          {
            width: '100%',
            height: scale(36),
            marginVertical: scale(10),
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: scale(SETTINGS_HEADER_MARGIN_BOTTOM),
            marginTop: scale(26),
          },
          style,
        ]}
      >
        <View style={{ flex: 1 }}>{isPhone ? <HeaderBackButton {...backButtonProps} /> : leftAction[name]?.()}</View>
        <View style={{ width: width / (isPhone ? 2 : 3) }}>
          <Text
            {...(truncateHeaderName[name] ? { numberOfLines: 1 } : {})}
            style={[font[isPhone ? 'Headline' : 'Headline Large'], { color: C('Label/Light/Primary'), textAlign: 'center' }]}
          >
            {i18n.t(header || headerName[name] || name)}
          </Text>
          {!!subHeader && (
            <View style={{ position: 'absolute', width: '100%', bottom: scale(-18) }}>
              <Text style={[font['Caption 1'], { color: subHeader.color, textAlign: 'center' }]}>{i18n.t(subHeader.text)}</Text>
            </View>
          )}
        </View>
        <View style={{ flex: 1 }}>{rightAction[name]?.()}</View>
      </View>
    </View>
  );
};

export default ColorWrapper(Header);
